import type { IChatMessage, IChatReaction } from "@/types/(my-space)/chat.interface";

/** The quick bar, in WhatsApp's order. Emoji are data, not copy. */
export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"] as const;

/** Emoji shown on a bubble's reaction pill before the count. */
export const REACTION_PILL_MAX = 3;

/** My emoji on a message, or null. */
export const myReaction = (
  reactions: IChatReaction[] | null | undefined,
  myUserId: number | undefined,
): string | null => {
  if (myUserId == null || !reactions) return null;
  return reactions.find((r) => r.userIds.includes(myUserId))?.emoji ?? null;
};

/**
 * `reactions` with my reaction set to `emoji` (null: removed) — one per
 * person, so any other emoji of mine goes. Empty groups drop; the rest stay
 * most used first, ties keeping their order (as the server sends them).
 */
export const withMyReaction = (
  reactions: IChatReaction[] | null | undefined,
  myUserId: number,
  emoji: string | null,
): IChatReaction[] => {
  const next = (reactions ?? [])
    .map((r) =>
      r.userIds.includes(myUserId)
        ? { ...r, userIds: r.userIds.filter((id) => id !== myUserId), count: r.count - 1 }
        : r,
    )
    .filter((r) => r.count > 0 && r.userIds.length > 0);
  if (emoji) {
    const i = next.findIndex((r) => r.emoji === emoji);
    if (i === -1) next.push({ emoji, count: 1, userIds: [myUserId] });
    else next[i] = { ...next[i], count: next[i].count + 1, userIds: [...next[i].userIds, myUserId] };
  }
  // Array.prototype.sort is stable: equal counts keep their order.
  return next.sort((a, b) => b.count - a.count);
};

/**
 * Picking an emoji: the same one as mine removes it (DELETE), anything else
 * sets it (PUT). `next` is the optimistic list.
 */
export const toggleReaction = (
  reactions: IChatReaction[] | null | undefined,
  myUserId: number,
  emoji: string,
): { action: "put" | "delete"; emoji: string | null; next: IChatReaction[] } => {
  const remove = myReaction(reactions, myUserId) === emoji;
  const mine = remove ? null : emoji;
  return {
    action: remove ? "delete" : "put",
    emoji: mine,
    next: withMyReaction(reactions, myUserId, mine),
  };
};

/**
 * Rolling back a failed change: put my previous reaction back onto whatever
 * the message holds NOW — others' reactions that arrived meanwhile survive,
 * which restoring a snapshot would drop.
 */
export const rollbackReaction = (
  current: IChatReaction[] | null | undefined,
  myUserId: number,
  previous: string | null,
) => withMyReaction(current, myUserId, previous);

/** The pill: the first few emoji and the total number of reactions. */
export const reactionSummary = (reactions: IChatReaction[] | null | undefined) => {
  const list = reactions ?? [];
  return {
    emojis: list.slice(0, REACTION_PILL_MAX).map((r) => r.emoji),
    total: list.reduce((n, r) => n + r.count, 0),
  };
};

/** Who reacted with what, me first, then in the server's (most used) order. */
export const reactionRows = (
  reactions: IChatReaction[] | null | undefined,
  myUserId: number | undefined,
) => {
  const rows = (reactions ?? []).flatMap((r) =>
    r.userIds.map((userId) => ({ userId, emoji: r.emoji })),
  );
  const mine = rows.filter((r) => r.userId === myUserId);
  return [...mine, ...rows.filter((r) => r.userId !== myUserId)];
};

/**
 * Reactions are offered on stored, live, non-system messages, to members who
 * can post (a broadcast's recipients can't).
 */
export const canReactTo = (
  message: Pick<IChatMessage, "id" | "type" | "isDeleted">,
  canSend: boolean,
) => canSend && message.id > 0 && !message.isDeleted && message.type !== "system";
